import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import ClientLogos from "./components/ClientLogos";
import CaseStudies from "./components/CaseStudies";
import PartnersSection from "./components/PartnersSection";
import Testimonials from "./components/Testimonials";
import Services from "./components/Services";
import WhyGrouth from "./components/WhyGrouth";
import InsightsSection from "./components/InsightsSection";
import ScrollingMarquee from "./components/ScrollingMarquee";
import ContactFooter from "./components/ContactFooter";
import WorkPage from "./components/WorkPage";
import InsightsPage from "./components/InsightsPage";
import ArticlePage from "./components/ArticlePage";
import NewsletterArchive from "./components/NewsletterArchive";
import NewsletterDetail from "./components/NewsletterDetail";
import NotFound from "./components/NotFound";
import Unsubscribe from "./components/Unsubscribe";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import CookiePolicy from "./components/CookiePolicy";
import GoogleAnalytics from "./components/GoogleAnalytics";

// Admin
import { AuthProvider } from "./admin/AuthContext";
import AdminLayout from "./admin/AdminLayout";
import AdminLogin from "./admin/AdminLogin";
import ProtectedAdminRoute from "./admin/ProtectedAdminRoute";
import AdminDashboard from "./admin/AdminDashboard";
import AdminContacts from "./admin/AdminContacts";
import AdminSubscribers from "./admin/AdminSubscribers";
import AdminArticles from "./admin/AdminArticles";
import AdminArticleEditor from "./admin/AdminArticleEditor";
import AdminCaseStudies from "./admin/AdminCaseStudies";
import AdminCaseEditor from "./admin/AdminCaseEditor";
import AdminBroadcast from "./admin/AdminBroadcast";
import AdminDigest from "./admin/AdminDigest";
import AdminAnalytics from "./admin/AdminAnalytics";
import AdminAuditLog from "./admin/AdminAuditLog";
import AdminSettings from "./admin/AdminSettings";
import AdminTeam from "./admin/AdminTeam";

// Scrolls to a section when the URL contains a hash (e.g. /#contact)
const HashScroller = () => {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempt < 20) {
        setTimeout(() => tryScroll(attempt + 1), 100);
      }
    };
    tryScroll();
  }, [location.pathname, location.hash]);
  return null;
};

// Public site shell — renders the marketing Navbar + a skip-link for accessibility
const PublicShell = ({ children }) => (
  <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:px-4 focus:py-2 focus:rounded-full focus:bg-[#83BF42] focus:text-white focus:font-['Inter'] focus:text-sm focus:shadow-lg"
      data-testid="skip-to-content"
    >
      Skip to content
    </a>
    <Navbar />
    <HashScroller />
    <main id="main-content" tabIndex={-1}>
      {children}
    </main>
  </>
);

const HomePage = () => (
  <>
    <HeroSection />
    <AboutSection />
    <ClientLogos />
    <CaseStudies />
    <PartnersSection />
    <Testimonials />
    <Services />
    <WhyGrouth />
    <InsightsSection />
    <ScrollingMarquee />
    <ContactFooter />
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <GoogleAnalytics />
          <Routes>
            {/* Public site */}
            <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
            <Route path="/work" element={<PublicShell><WorkPage /></PublicShell>} />
            <Route path="/insights" element={<PublicShell><InsightsPage /></PublicShell>} />
            <Route path="/insights/:slug" element={<PublicShell><ArticlePage /></PublicShell>} />
            <Route path="/newsletter" element={<PublicShell><NewsletterArchive /></PublicShell>} />
            <Route path="/newsletter/:id" element={<PublicShell><NewsletterDetail /></PublicShell>} />
            <Route path="/unsubscribe/:token" element={<PublicShell><Unsubscribe /></PublicShell>} />
            <Route path="/privacy" element={<PublicShell><Privacy /></PublicShell>} />
            <Route path="/privacy-policy" element={<PublicShell><Privacy /></PublicShell>} />
            <Route path="/terms" element={<PublicShell><Terms /></PublicShell>} />
            <Route path="/terms-and-conditions" element={<PublicShell><Terms /></PublicShell>} />
            <Route path="/cookie-policy" element={<PublicShell><CookiePolicy /></PublicShell>} />
            <Route path="/cookies" element={<PublicShell><CookiePolicy /></PublicShell>} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="subscribers" element={<AdminSubscribers />} />
              <Route path="broadcast" element={<AdminBroadcast />} />
              <Route path="digest" element={<AdminDigest />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="articles/new" element={<AdminArticleEditor />} />
              <Route path="articles/:slug" element={<AdminArticleEditor />} />
              <Route path="cases" element={<AdminCaseStudies />} />              <Route path="cases/new" element={<AdminCaseEditor />} />
              <Route path="cases/:id" element={<AdminCaseEditor />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="activity" element={<AdminAuditLog />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
